import React, { useEffect, useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  FileCheck2, Sparkles, RefreshCw, Save, Copy, Download, ExternalLink, CheckCircle2, Circle, ClipboardList,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL, withCredentials: true });
const GOOGLE_FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSdkeXQZOnK9AAk2ZaCMf2uMQgMv22SzE_yPNTDk_Fb_wTtLmQ/viewform';

export const IncorporationQuestionnaire = ({ projectId, projectName }) => {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [autoAnswering, setAutoAnswering] = useState(false);
  const saveTimer = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/api/admin/nonprofit-accelerator/projects/${projectId}/incorporation-questionnaire`);
      setData(r.data);
      setAnswers(r.data.answers || {});
    } catch (e) {
      toast.error('Could not load questionnaire: ' + (e.response?.data?.detail || e.message));
    } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const persist = async (next) => {
    setSaving(true);
    try {
      await api.patch(`/api/admin/nonprofit-accelerator/projects/${projectId}/incorporation-questionnaire`, { answers: next });
    } catch { /* ignore transient */ } finally { setSaving(false); }
  };

  const setAnswer = (qid, val) => {
    const next = { ...answers, [qid]: val };
    setAnswers(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persist(next), 900);
  };

  const pollJob = (jobId, onDone, onErr) => {
    let attempts = 0; let transient = 0;
    const tick = async () => {
      attempts += 1;
      try {
        const jr = await api.get(`/api/admin/nonprofit-accelerator/draft-jobs/${jobId}`);
        transient = 0;
        if (jr.data.status === 'done') onDone(jr.data);
        else if (jr.data.status === 'error') (onErr || (() => {}))(jr.data.error || 'failed');
        else if (attempts > 60) (onErr || (() => {}))('timed out');
        else setTimeout(tick, 3000);
      } catch (e) {
        transient += 1;
        if (transient >= 5) (onErr || (() => {}))(e.response?.data?.detail || e.message);
        else setTimeout(tick, 3000);
      }
    };
    setTimeout(tick, 3000);
  };

  const autoAnswer = async () => {
    if (autoAnswering) return;
    setAutoAnswering(true);
    try {
      const r = await api.post(`/api/admin/nonprofit-accelerator/projects/${projectId}/incorporation-questionnaire/auto-answer`);
      toast.success('AI is drafting your answers from your project data…');
      pollJob(r.data.job_id, (jr) => {
        setAnswers(jr.answers || {});
        setAutoAnswering(false);
        toast.success(`AI filled ${jr.filled?.length || 0} field(s). Review, tweak, and you're ready to submit.`);
      }, (err) => { toast.error('Auto-answer failed: ' + err); setAutoAnswering(false); });
    } catch (e) {
      toast.error('Could not start: ' + (e.response?.data?.detail || e.message));
      setAutoAnswering(false);
    }
  };

  const copyAll = () => {
    if (!data) return;
    const text = data.questions.map((q) => `${q.label}${q.required ? ' *' : ''}\n${(answers[q.id] || '').trim() || '(to be completed)'}`).join('\n\n');
    navigator.clipboard.writeText(text);
    toast.success('All answers copied — paste into the form');
  };

  const downloadPdf = () => {
    window.open(`${API_URL}/api/admin/nonprofit-accelerator/projects/${projectId}/incorporation-questionnaire-pdf`, '_blank');
  };

  if (loading) {
    return <div className="bg-white border border-[#E2E8F0] rounded-2xl p-10 text-center text-sm text-[#64748B]" data-testid="npa-incorp-loading"><RefreshCw size={18} className="animate-spin inline mr-2" /> Loading questionnaire…</div>;
  }
  if (!data) return null;

  const questions = data.questions || [];
  const required = questions.filter((q) => q.required);
  const doneRequired = required.filter((q) => (answers[q.id] || '').trim()).length;
  const pct = required.length ? Math.round((doneRequired / required.length) * 100) : 0;

  const sections = [];
  const seen = new Set();
  questions.forEach((q) => { if (!seen.has(q.section)) { seen.add(q.section); sections.push(q.section); } });

  return (
    <div data-testid="npa-incorp-page" className="space-y-5">
      {/* Header / command bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-2xl overflow-hidden">
        <div className="h-1.5 w-full bg-gradient-to-r from-[#1D4ED8] via-fuchsia-500 to-[#F97316]" />
        <div className="p-5 sm:p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-lg font-black text-[#0A0F1C] flex items-center gap-2"><FileCheck2 size={19} className="text-[#1D4ED8]" /> {data.title}</h2>
              <p className="text-xs text-[#64748B] mt-1 max-w-2xl">Every question from {data.source}'s incorporation &amp; 501(c)(3) questionnaire — for <strong>{projectName}</strong>. Let AI draft the answers from your project, review &amp; edit, then copy them straight into the official form.</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={autoAnswer} disabled={autoAnswering} data-testid="npa-incorp-autoanswer" className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold bg-fuchsia-600 text-white rounded-lg hover:bg-fuchsia-700 disabled:opacity-60 transition-colors">
                {autoAnswering ? <><RefreshCw size={13} className="animate-spin" /> Drafting…</> : <><Sparkles size={13} /> AI auto-answer</>}
              </button>
              <button type="button" onClick={copyAll} data-testid="npa-incorp-copy-all" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#E2E8F0] text-[#475569] rounded-lg hover:bg-[#F8FAFC] transition-colors"><Copy size={13} /> Copy all</button>
              <button type="button" onClick={downloadPdf} data-testid="npa-incorp-pdf" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold border border-[#1D4ED8] text-[#1D4ED8] rounded-lg hover:bg-[#EFF6FF] transition-colors"><Download size={13} /> PDF for consultant</button>
              <a href={GOOGLE_FORM_URL} target="_blank" rel="noopener noreferrer" data-testid="npa-incorp-openform" className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold bg-[#0A0F1C] text-white rounded-lg hover:opacity-90 transition-colors"><ExternalLink size={13} /> Open the form</a>
            </div>
          </div>
          {/* Progress */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-[11px] font-bold text-[#64748B] mb-1.5">
              <span data-testid="npa-incorp-progress-label">{doneRequired}/{required.length} required answered</span>
              <span className="flex items-center gap-2">{saving ? <><RefreshCw size={11} className="animate-spin" /> Saving…</> : <span className="text-emerald-600 flex items-center gap-1"><CheckCircle2 size={12} /> Saved</span>}</span>
            </div>
            <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#1D4ED8] to-fuchsia-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} data-testid="npa-incorp-progress-bar" />
            </div>
          </div>
        </div>
      </div>

      {/* Questions grouped by section */}
      {sections.map((section) => (
        <div key={section} className="bg-white border border-[#E2E8F0] rounded-2xl p-5 sm:p-6" data-testid={`npa-incorp-section-${section.replace(/\s+/g, '-').toLowerCase()}`}>
          <h3 className="text-xs font-black uppercase tracking-wider text-[#1D4ED8] mb-4 flex items-center gap-2"><ClipboardList size={14} /> {section}</h3>
          <div className="space-y-5">
            {questions.filter((q) => q.section === section).map((q) => {
              const val = answers[q.id] || '';
              const filled = val.trim().length > 0;
              return (
                <div key={q.id} data-testid={`npa-incorp-q-${q.id}`}>
                  <label className="flex items-start gap-2 text-sm font-semibold text-[#0A0F1C] mb-1.5">
                    {filled ? <CheckCircle2 size={15} className="text-emerald-500 shrink-0 mt-0.5" /> : <Circle size={15} className="text-slate-300 shrink-0 mt-0.5" />}
                    <span>{q.label} {q.required && <span className="text-red-500">*</span>}</span>
                  </label>
                  {q.type === 'choice' ? (
                    <div className="flex flex-wrap gap-2 pl-6" data-testid={`npa-incorp-choices-${q.id}`}>
                      {q.options.map((opt) => (
                        <button type="button" key={opt} onClick={() => setAnswer(q.id, opt)} className={`text-left text-xs font-semibold px-3 py-2 rounded-lg border transition-colors ${val === opt ? 'border-[#1D4ED8] bg-[#EFF6FF] text-[#1D4ED8]' : 'border-[#E2E8F0] text-[#475569] hover:border-[#BFDBFE]'}`}>
                          {opt}
                        </button>
                      ))}
                    </div>
                  ) : q.type === 'paragraph' ? (
                    <textarea value={val} onChange={(e) => setAnswer(q.id, e.target.value)} rows={4} data-testid={`npa-incorp-input-${q.id}`} className="w-full ml-6 max-w-[calc(100%-1.5rem)] px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8] focus:ring-1 focus:ring-[#1D4ED8]" placeholder="Type or use AI auto-answer…" />
                  ) : (
                    <input value={val} onChange={(e) => setAnswer(q.id, e.target.value)} data-testid={`npa-incorp-input-${q.id}`} className="w-full ml-6 max-w-[calc(100%-1.5rem)] px-3 py-2 border border-[#E2E8F0] rounded-lg text-sm focus:outline-none focus:border-[#1D4ED8] focus:ring-1 focus:ring-[#1D4ED8]" placeholder={q.type === 'date' ? 'e.g. 06/01/2026' : 'Your answer…'} />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <p className="text-[11px] text-[#94A3B8] text-center pb-2">AI drafts narrative answers from your project data and leaves legal/PII fields (EIN, dates, addresses) for you to confirm. Always review before submitting.</p>
    </div>
  );
};

export default IncorporationQuestionnaire;
